import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import { normalizeSearch, searchesShareEntity, searchHasAppliedFilters } from '@/utils/searchTabIdentity';

export { normalizeSearch } from '@/utils/searchTabIdentity';

export const SEARCH_TABS_STORAGE_KEY = 'disgenet_search_tabs';
export const SEARCH_TABS_MAX = 10;

export type SearchTab = {
  id: string;
  title: string;
  search: string;
  updatedAt: number;
};

export type OpenOrReplacePayload = {
  search: string;
  title: string;
};

export type OpenTabResult = { ok: true; id: string } | { ok: false; reason: 'max_tabs' | 'not_found' };

export interface SearchTabsState {
  tabs: SearchTab[];
  activeTabId: string | null;
  maxTabs: number;

  openTab: (payload: OpenOrReplacePayload) => OpenTabResult;
  replaceActiveTab: (payload: OpenOrReplacePayload) => string;
  activateTab: (id: string) => boolean;
  closeTab: (id: string) => { nextActiveId: string | null; closedActive: boolean };
  duplicateTab: (id: string) => OpenTabResult;
  reorderTabs: (fromIndex: number, toIndex: number) => boolean;
  syncActiveFromUrl: (search: string, title?: string) => void;
  setTabTitle: (id: string, title: string) => void;
  flushActiveSearch: (search: string) => void;
  resetTabs: () => void;
}

function createTabId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `tab_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function createTab(payload: OpenOrReplacePayload): SearchTab {
  return {
    id: createTabId(),
    title: payload.title.trim() || 'All results',
    search: normalizeSearch(payload.search),
    updatedAt: Date.now(),
  };
}

export const useSearchTabsStore = create<SearchTabsState>()(
  devtools(
    persist(
      (set, get) => ({
        tabs: [],
        activeTabId: null,
        maxTabs: SEARCH_TABS_MAX,

        openTab: payload => {
          const { tabs, maxTabs } = get();
          if (tabs.length >= maxTabs) {
            return { ok: false, reason: 'max_tabs' };
          }
          const tab = createTab(payload);
          set({
            tabs: [...tabs, tab],
            activeTabId: tab.id,
          });
          return { ok: true, id: tab.id };
        },

        replaceActiveTab: payload => {
          const { tabs, activeTabId } = get();
          const search = normalizeSearch(payload.search);
          const title = payload.title.trim() || 'All results';
          const now = Date.now();

          if (!activeTabId || tabs.length === 0) {
            const tab = createTab({ search, title });
            set({ tabs: [tab], activeTabId: tab.id });
            return tab.id;
          }

          const index = tabs.findIndex(t => t.id === activeTabId);
          if (index < 0) {
            const tab = createTab({ search, title });
            set({ tabs: [...tabs, tab], activeTabId: tab.id });
            return tab.id;
          }

          const nextTabs = [...tabs];
          nextTabs[index] = {
            ...nextTabs[index],
            search,
            title,
            updatedAt: now,
          };
          set({ tabs: nextTabs });
          return nextTabs[index].id;
        },

        activateTab: id => {
          const { tabs } = get();
          if (!tabs.some(t => t.id === id)) return false;
          set({ activeTabId: id });
          return true;
        },

        closeTab: id => {
          const { tabs, activeTabId } = get();
          const index = tabs.findIndex(t => t.id === id);
          if (index < 0) {
            return { nextActiveId: activeTabId, closedActive: false };
          }

          const closedActive = activeTabId === id;
          const nextTabs = tabs.filter(t => t.id !== id);

          if (nextTabs.length === 0) {
            set({ tabs: [], activeTabId: null });
            return { nextActiveId: null, closedActive };
          }

          let nextActiveId = activeTabId;
          if (closedActive) {
            const neighbor = nextTabs[Math.min(index, nextTabs.length - 1)];
            nextActiveId = neighbor.id;
          }

          set({ tabs: nextTabs, activeTabId: nextActiveId });
          return { nextActiveId, closedActive };
        },

        duplicateTab: id => {
          const { tabs, maxTabs } = get();
          if (tabs.length >= maxTabs) {
            return { ok: false, reason: 'max_tabs' };
          }
          const index = tabs.findIndex(t => t.id === id);
          if (index < 0) {
            return { ok: false, reason: 'not_found' };
          }
          const source = tabs[index];
          const copy: SearchTab = {
            id: createTabId(),
            title: source.title,
            search: source.search,
            updatedAt: Date.now(),
          };
          const nextTabs = [...tabs.slice(0, index + 1), copy, ...tabs.slice(index + 1)];
          set({ tabs: nextTabs, activeTabId: copy.id });
          return { ok: true, id: copy.id };
        },

        reorderTabs: (fromIndex, toIndex) => {
          const { tabs } = get();
          if (
            fromIndex < 0 ||
            toIndex < 0 ||
            fromIndex >= tabs.length ||
            toIndex >= tabs.length ||
            fromIndex === toIndex
          ) {
            return false;
          }
          const nextTabs = [...tabs];
          const [moved] = nextTabs.splice(fromIndex, 1);
          nextTabs.splice(toIndex, 0, moved);
          set({ tabs: nextTabs });
          return true;
        },

        syncActiveFromUrl: (search, title) => {
          const normalized = normalizeSearch(search);
          const { tabs, activeTabId } = get();
          const now = Date.now();

          if (tabs.length === 0) {
            const tab = createTab({
              search: normalized,
              title: title?.trim() || 'All results',
            });
            set({ tabs: [tab], activeTabId: tab.id });
            return;
          }

          const matching = tabs.find(t => normalizeSearch(t.search) === normalized);
          if (matching) {
            if (matching.id !== activeTabId) {
              set({ activeTabId: matching.id });
            }
            if (title && title.trim() && matching.title !== title.trim()) {
              set({
                tabs: tabs.map(t =>
                  t.id === matching.id ? { ...t, title: title.trim(), updatedAt: now } : t,
                ),
              });
            }
            return;
          }

          if (!activeTabId) {
            const tab = createTab({
              search: normalized,
              title: title?.trim() || 'All results',
            });
            set({ tabs: [...tabs, tab], activeTabId: tab.id });
            return;
          }

          const index = tabs.findIndex(t => t.id === activeTabId);
          if (index < 0) {
            const tab = createTab({
              search: normalized,
              title: title?.trim() || 'All results',
            });
            set({ tabs: [...tabs, tab], activeTabId: tab.id });
            return;
          }

          if (
            searchHasAppliedFilters(tabs[index].search) &&
            !searchHasAppliedFilters(normalized) &&
            searchesShareEntity(tabs[index].search, normalized)
          ) {
            return;
          }

          const nextTabs = [...tabs];
          nextTabs[index] = {
            ...nextTabs[index],
            search: normalized,
            ...(title?.trim() ? { title: title.trim() } : {}),
            updatedAt: now,
          };
          set({ tabs: nextTabs });
        },

        setTabTitle: (id, title) => {
          const trimmed = title.trim() || 'All results';
          set(state => ({
            tabs: state.tabs.map(t => (t.id === id ? { ...t, title: trimmed, updatedAt: Date.now() } : t)),
          }));
        },

        flushActiveSearch: search => {
          const normalized = normalizeSearch(search);
          if (!normalized || normalized === '?') return;
          if (!normalized.includes('view=')) return;

          const { tabs, activeTabId } = get();
          if (!activeTabId) return;
          const index = tabs.findIndex(t => t.id === activeTabId);
          if (index < 0) return;
          if (normalizeSearch(tabs[index].search) === normalized) return;
          if (!searchesShareEntity(tabs[index].search, normalized)) return;

          const nextTabs = [...tabs];
          nextTabs[index] = {
            ...nextTabs[index],
            search: normalized,
            updatedAt: Date.now(),
          };
          set({ tabs: nextTabs });
        },

        resetTabs: () => set({ tabs: [], activeTabId: null }),
      }),
      {
        name: SEARCH_TABS_STORAGE_KEY,
        partialize: state => ({
          tabs: state.tabs,
          activeTabId: state.activeTabId,
        }),
      },
    ),
    { name: 'search-tabs-store' },
  ),
);

export const useSearchTabsBar = () =>
  useSearchTabsStore(
    useShallow(state => ({
      tabs: state.tabs,
      activeTabId: state.activeTabId,
      maxTabs: state.maxTabs,
      canOpenTab: state.tabs.length < state.maxTabs,
    })),
  );
